import { percentToPoint } from './gradeMapping';

// =============== Helpers ===============

export const parseFloatOrNaN = (val) => {
  const s = String(val ?? "")
    .trim()
    .replace(",", ".");
  if (s === "") return NaN;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : NaN;
};

export const parseIntOrNaN = (val) => {
  const s = String(val ?? "").trim();
  if (s === "") return NaN;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : NaN;
};

export const parseFloatLoose0 = (val) => {
  const s = String(val ?? "")
    .trim()
    .replace(",", ".");
  if (s === "") return 0;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
};

export const roundForPolicy = (x) => Math.round(x + 1e-9);

// =============== Calculations ===============

export const calculateCGPA = (cgpaData) => {
  const currCGPA = parseFloatOrNaN(cgpaData.currentCGPA);
  const prevCH = parseIntOrNaN(cgpaData.completedCreditHours);
  const semGPA = parseFloatOrNaN(cgpaData.currentSemesterGPA);
  const semCH = parseIntOrNaN(cgpaData.currentSemesterCreditHours);

  const safePrevCH = Number.isFinite(prevCH) ? prevCH : 0;
  const safeSemCH = Number.isFinite(semCH) ? semCH : 0;

  const gpaInRange = (x) => Number.isFinite(x) && x >= 0 && x <= 4;

  if (safePrevCH < 0 || safeSemCH < 0) throw new Error("Credit hours cannot be negative.");
  
  const totalCH = safePrevCH + safeSemCH;
  if (totalCH === 0) throw new Error("Please enter some credit hours.");

  const prevQP = (gpaInRange(currCGPA) ? currCGPA : 0) * safePrevCH;
  const semQP = (gpaInRange(semGPA) ? semGPA : 0) * safeSemCH;
  const newCGPAVal = (prevQP + semQP) / totalCH;

  let improvementStr = "—";
  if (safePrevCH > 0 && gpaInRange(currCGPA)) {
    const delta = newCGPAVal - currCGPA;
    improvementStr = `${delta >= 0 ? "+" : ""}${delta.toFixed(2)}`;
  }

  return { newCGPA: newCGPAVal, improvement: improvementStr };
};

export const calculateTheoryGPA = (data) => {
  // New Policy: Assignments (10%), Quizzes (15%), Midterm (25%), Final (50%)
  // Each component allows flexible totals
  
  const assignObtained = parseFloatLoose0(data.assignmentObtained);
  const assignTotal = parseFloatLoose0(data.assignmentTotal);
  const quizObtained = parseFloatLoose0(data.quizObtained);
  const quizTotal = parseFloatLoose0(data.quizTotal);
  const midObtained = parseFloatLoose0(data.midtermObtained);
  const midTotal = parseFloatLoose0(data.midtermTotal);
  const finalObtained = parseFloatLoose0(data.finalObtained);
  const finalTotal = parseFloatLoose0(data.finalTotal);

  // Validate that totals are not zero
  if (assignTotal === 0 && assignObtained > 0) throw new Error("Assignment total cannot be 0");
  if (quizTotal === 0 && quizObtained > 0) throw new Error("Quiz total cannot be 0");
  if (midTotal === 0 && midObtained > 0) throw new Error("Midterm total cannot be 0");
  if (finalTotal === 0 && finalObtained > 0) throw new Error("Final total cannot be 0");

  // Calculate percentage for each component, then apply weightage
  const assignPercent = assignTotal > 0 ? (assignObtained / assignTotal) * 10 : 0;
  const quizPercent = quizTotal > 0 ? (quizObtained / quizTotal) * 15 : 0;
  const midPercent = midTotal > 0 ? (midObtained / midTotal) * 25 : 0;
  const finalPercent = finalTotal > 0 ? (finalObtained / finalTotal) * 50 : 0;

  // Total weighted percentage out of 100
  const totalPercentRaw = assignPercent + quizPercent + midPercent + finalPercent;
  
  // Round to whole number for GPA calculation (policy)
  const totalPercentRounded = roundForPolicy(totalPercentRaw);
  const gpa = percentToPoint(totalPercentRounded);

  return {
    gpa,
    percentageRaw: totalPercentRaw,
    percentageRounded: totalPercentRounded,
    breakdown: {
      assignment: assignPercent,
      quiz: quizPercent,
      midterm: midPercent,
      final: finalPercent
    }
  };
};

export const calculateLabGPA = (data) => {
  // New Policy for Lab Subjects
  // Theory: Assignments (10%), Quizzes (15%), Midterm (25%), Final (50%)
  // Lab: Assignment (25%), Midterm (25%), Final (50%)
  // Final GPA weighted by credit hour distribution
  
  const totalCH = parseFloatLoose0(data.totalCH);
  const theoryCH = parseFloatLoose0(data.theoryCH);
  const labCH = parseFloatLoose0(data.labCH);

  // Validate credit hours
  if (totalCH <= 0) throw new Error("Total credit hours must be greater than 0");
  if (theoryCH < 0 || labCH < 0) throw new Error("Credit hours cannot be negative");
  if (theoryCH + labCH !== totalCH) throw new Error("Theory + Lab credit hours must equal total");

  // Theory components (flexible totals)
  const theoryAssignObt = parseFloatLoose0(data.theoryAssignmentObtained);
  const theoryAssignTotal = parseFloatLoose0(data.theoryAssignmentTotal);
  const theoryQuizObt = parseFloatLoose0(data.theoryQuizObtained);
  const theoryQuizTotal = parseFloatLoose0(data.theoryQuizTotal);
  const theoryMidObt = parseFloatLoose0(data.theoryMidtermObtained);
  const theoryMidTotal = parseFloatLoose0(data.theoryMidtermTotal);
  const theoryFinalObt = parseFloatLoose0(data.theoryFinalObtained);
  const theoryFinalTotal = parseFloatLoose0(data.theoryFinalTotal);

  // Lab components (flexible totals)
  const labAssignObt = parseFloatLoose0(data.labAssignmentObtained);
  const labAssignTotal = parseFloatLoose0(data.labAssignmentTotal);
  const labMidObt = parseFloatLoose0(data.labMidtermObtained);
  const labMidTotal = parseFloatLoose0(data.labMidtermTotal);
  const labFinalObt = parseFloatLoose0(data.labFinalObtained);
  const labFinalTotal = parseFloatLoose0(data.labFinalTotal);

  // Calculate theory percentage (0-100)
  const theoryAssignPercent = theoryAssignTotal > 0 ? (theoryAssignObt / theoryAssignTotal) * 10 : 0;
  const theoryQuizPercent = theoryQuizTotal > 0 ? (theoryQuizObt / theoryQuizTotal) * 15 : 0;
  const theoryMidPercent = theoryMidTotal > 0 ? (theoryMidObt / theoryMidTotal) * 25 : 0;
  const theoryFinalPercent = theoryFinalTotal > 0 ? (theoryFinalObt / theoryFinalTotal) * 50 : 0;
  const theoryTotalPercent = theoryAssignPercent + theoryQuizPercent + theoryMidPercent + theoryFinalPercent;

  // Calculate lab percentage (0-100)
  const labAssignPercent = labAssignTotal > 0 ? (labAssignObt / labAssignTotal) * 25 : 0;
  const labMidPercent = labMidTotal > 0 ? (labMidObt / labMidTotal) * 25 : 0;
  const labFinalPercent = labFinalTotal > 0 ? (labFinalObt / labFinalTotal) * 50 : 0;
  const labTotalPercent = labAssignPercent + labMidPercent + labFinalPercent;

  // Weight by credit hours
  const theoryWeight = theoryCH / totalCH;
  const labWeight = labCH / totalCH;
  const finalPercentRaw = (theoryTotalPercent * theoryWeight) + (labTotalPercent * labWeight);

  // Round and convert to GPA
  const finalPercentRounded = roundForPolicy(finalPercentRaw);
  const gpa = percentToPoint(finalPercentRounded);

  return {
    gpa,
    percentageRaw: finalPercentRaw,
    percentageRounded: finalPercentRounded,
    breakdown: {
      theory: {
        assignment: theoryAssignPercent,
        quiz: theoryQuizPercent,
        midterm: theoryMidPercent,
        final: theoryFinalPercent,
        total: theoryTotalPercent
      },
      lab: {
        assignment: labAssignPercent,
        midterm: labMidPercent,
        final: labFinalPercent,
        total: labTotalPercent
      },
      weights: {
        theory: theoryWeight * 100,
        lab: labWeight * 100
      }
    }
  };
};

export const calculateSemesterGPA = (subjects) => {
  let totalQualityPoints = 0;
  let totalCreditHours = 0;

  for (const sub of subjects) {
    const ch = parseIntOrNaN(sub.creditHours);
    const sgpa = parseFloatOrNaN(sub.subjectGPA);

    if (Number.isFinite(ch) && Number.isFinite(sgpa)) {
      totalQualityPoints += sgpa * ch;
      totalCreditHours += ch;
    }
  }

  if (totalCreditHours === 0) return { gpa: 0, totalCreditHours: 0 };
  
  return { gpa: totalQualityPoints / totalCreditHours, totalCreditHours };
};

export const calculateGradeTargets = (currentWeightedTotal, finalWeight, finalTotal) => {
  const targets = [
    { grade: 'A', threshold: 85, points: '4.00' },
    { grade: 'A-', threshold: 80, points: '3.67' },
    { grade: 'B+', threshold: 75, points: '3.33' },
    { grade: 'B', threshold: 71, points: '3.00' },
    { grade: 'B-', threshold: 68, points: '2.67' },
    { grade: 'C+', threshold: 64, points: '2.33' },
    { grade: 'C', threshold: 61, points: '2.00' },
    { grade: 'C-', threshold: 58, points: '1.67' },
    { grade: 'D+', threshold: 54, points: '1.33' },
    { grade: 'D', threshold: 50, points: '1.00' },
  ];

  return targets.map(t => {
    // CUI rounds to the nearest integer. So 84.5 becomes 85.
    // The true minimum weighted percentage needed is (threshold - 0.5).
    const minNeededPercentage = t.threshold - 0.5;
    
    // Required Marks = (TargetMinPercentage - CurrentWeighted) * (FinalTotal / FinalWeight)
    const neededMarks = (minNeededPercentage - currentWeightedTotal) * (finalTotal / finalWeight);
    const neededPercent = (neededMarks / finalTotal) * 100;

    let status = 'achievable';
    if (neededMarks > finalTotal) {
      status = 'impossible';
    }

    return {
      ...t,
      neededMarks: Math.max(0, neededMarks),
      neededPercent: Math.max(0, neededPercent),
      status
    };
  });
};
